import {
  Banner,
  Button,
  Text,
  Flex,
  Icon,
  Input,
  ModalManagerContext,
  Form,
  Checkbox,
  CheckboxGroup,
  Card,
  useElement,
  Tooltip,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import React, { useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { appState, customModalProps, SDKComponentProps } from '../../../../interfaces/common';
import { updateLoaderDetails, updateBannerInfo, setChatInfo } from '../../../../redux/actions';
import { InteractiveText } from '../../../Utilities/custom.styled';
import { StyledTable } from '../../../Utilities/custom.styled';
import { CustomModal } from '../../../Utilities/CustomModal';
import { addNewTag, deleteTag, updateGeneralInfo } from '../ChatApis';
import TagSnippet from '../Modals/TagSnippet';
import { systemTag, chatTag } from '../../../../interfaces/chat';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { usePlatformVersionSelector } from '../../../../redux/selectors';

interface GeneralInfoFormData {
  info: string[];
}

const GeneralInfo: React.FC<SDKComponentProps> = ({ platform }: SDKComponentProps) => {
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { systemTags = {}, connectionId, encryptionEnabled } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);
  const { register, handleSubmit, setValue } = useForm<GeneralInfoFormData>({ mode: 'all' });
  const navigate = useNavigate();
  const [showTooltipEl, setShowTooltipEl] = useElement();

  const actions = (
    <Button
      name="Save"
      type="submit"
      variant="primary"
      style={{ marginTop: '10px' }}
      disabled={encryptionEnabled && !!platform}
    >
      {translate('save')}
    </Button>
  );

  useEffect(() => {
    let enabledInfo: string[] = [];
    Object.entries(systemTags).map(([key, value]) => {
      value && enabledInfo.push(key);
      return null;
    });
    setValue('info', enabledInfo);
  }, [systemTags, setValue]);

  const options = {
    ipAddress: translate('ip_address'),
    location: translate('location'),
    device: translate('device'),
    currentPage: translate('current_page'),
  };

  const optionsSdk = {
    ipAddress: translate('ip_address'),
    location: translate('location'),
    device: translate('device_os'),
  };
  const disableForBYOK = [options.ipAddress, options.location, options.device, optionsSdk.device];
  const byokInfoText = translate('byok_context_data_info').replace('XXX', translate(platform ? 'device_os' : 'device'));
  const onSave = async (data: GeneralInfoFormData) => {
    let payload: systemTag = {};
    Object.keys(options).map((item: string) => {
      payload[item] = data?.info?.indexOf(item) !== -1;
      return null;
    });

    dispatch(updateLoaderDetails(true));
    const json = await updateGeneralInfo(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('general_information_updated'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('general_information_update_failed'), 'urgent'));
    }
  };
  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 1,
      }}
    >
      <Text variant="h2">{translate('general_information')}</Text>
      {!platform && <Text>{translate('context_data_captured_without_website_configuration_text')}</Text>}
      <Form container={{ direction: 'column', itemGap: '1rem' }} actions={actions} onSubmit={handleSubmit(onSave)}>
        {encryptionEnabled && <Tooltip target={showTooltipEl}>{byokInfoText}</Tooltip>}
        <CheckboxGroup name="info" ref={setShowTooltipEl}>
          {Object.entries(platform ? optionsSdk : options).map(([identifier, label], i) => {
            return (
              <Checkbox
                key={i}
                label={label}
                value={identifier}
                ref={register()}
                disabled={encryptionEnabled && disableForBYOK.includes(label)}
              />
            );
          })}
        </CheckboxGroup>
      </Form>
    </Flex>
  );
};

const ChatContextData: React.FC<SDKComponentProps> = ({ platform }: SDKComponentProps) => {
  const { chatTags = [], connectionId } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const { register, handleSubmit, errors, reset, formState } = useForm({ mode: 'all' });
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const { fullVersion, majorVersion, minorVersion } = usePlatformVersionSelector();
  const showWarning =
    !fullVersion || (majorVersion && (majorVersion < 8 || (majorVersion === 8 && minorVersion && minorVersion < 6)));

  const onAdd = async (data: chatTag) => {
    reset({ name: '', tag: '' });
    dispatch(updateLoaderDetails(true));
    const json = await addNewTag(sessionId, connectionId, data);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${data.tag} ${translate('tag_added')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(`${translate('tag_addition_failed')} ${data.tag}`, 'urgent'));
    }
  };

  const deleteTagRecord = async (tag: string) => {
    const payload = { tag: tag };
    dispatch(updateLoaderDetails(true));
    const json = await deleteTag(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${tag} ${translate('tag_deleted')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(`${translate('tag_delete_failed')} ${tag}`, 'urgent'));
    }
  };

  const data = chatTags.map((item, index) => {
    return {
      id: index,
      name: <Text>{item.name}</Text>,
      tag: (
        <>
          <Flex container={{ direction: 'row', justify: 'between', alignItems: 'center' }}>
            <Text>{item.tag}</Text>
            <Button variant="simple" icon onClick={() => deleteTagRecord(item.tag)}>
              <Icon name="trash" />
            </Button>
          </Flex>
        </>
      ),
    };
  });

  const columns: ColumnProps<(typeof data)[number]>[] = [
    { renderer: 'name', label: translate('display_name') },
    { renderer: 'tag', label: translate('tag') },
  ];

  const warningAction = (
    <Text as="h4" variant="h4">
      {translate('context_data_warning_text')}
    </Text>
  );

  const tagValidation = (tag: string) => {
    const validationMsg: string = translate('valid_tag_error');
    const systemMsg: string = translate('non_reserved_tag_error');
    const notAllowedTags = ['ipAddress', 'device', 'currentPage', 'location'];

    const tagPattern = /^[A-Za-z](\w+$)/;

    if (notAllowedTags.indexOf(tag) !== -1) {
      return systemMsg;
    }

    return !!tagPattern.test(tag) ? true : validationMsg;
  };

  const copyCodeSnippet = () => {
    modalMethods.current = create<customModalProps>(CustomModal, {
      heading: translate('copy_code_snippet_for_tags'),
      children: <TagSnippet currentTab="contextData" />,
    });
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 3,
      }}
    >
      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
        }}
      >
        {showWarning && <Banner id="warning4" variant="warning" action={warningAction} messages={[]} />}
        <Text>
          {translate(
            platform
              ? 'context_data_configuration_title_text_mobile_sdk'
              : 'context_data_configuration_title_text_chat',
          )}
          {/* <Link href="#">{translate('documentation_link_text')}</Link> */}
        </Text>
      </Flex>
      <GeneralInfo platform={platform} />
      <Flex
        container={{
          direction: 'column',
          itemGap: 1,
        }}
      >
        <Text variant="h2">{translate('tags')}</Text>
        <Text>{translate(platform ? 'tags_description_text_sdk' : 'tags_description_text')}</Text>
        <Flex
          container={{
            direction: 'row',
            alignItems: 'start',
            itemGap: 1,
          }}
        >
          <Flex item={{ grow: 1 }}>
            <Input
              label={translate('display_name')}
              type="text"
              name="name"
              required={true}
              autoComplete="off"
              info={errors.name?.message}
              status={errors.name?.message ? 'error' : undefined}
              ref={register({ required: translate('name_empty') })}
            />
          </Flex>
          <Flex item={{ grow: 1 }}>
            <Input
              label={translate('tag')}
              type="text"
              name="tag"
              required={true}
              autoComplete="off"
              info={errors.tag?.message}
              status={errors.tag?.message ? 'error' : undefined}
              placeholder={translate('no_spaces_special_chars')}
              ref={register({ required: translate('tag_empty_error'), validate: tagValidation })}
            />
          </Flex>
          <Flex container style={{ paddingTop: '20px' }}>
            <Button type="submit" onClick={handleSubmit(onAdd)} disabled={!formState.isValid}>
              {translate('add')}
            </Button>
          </Flex>
        </Flex>
        {data.length > 0 && !platform && (
          <InteractiveText className="pointer" onClick={copyCodeSnippet}>
            {translate('copy_code_snippet')}
          </InteractiveText>
        )}
        <Card style={{ padding: 0 }}>
          <StyledTable
            hoverHighlight={true}
            padding={'default'}
            columns={columns}
            data={data}
            emptyContent={
              <Text variant="h5" style={{ padding: '0.5rem 0' }}>
                {translate('no_tags_configured')}
              </Text>
            }
          />
        </Card>
      </Flex>
    </Flex>
  );
};

export default ChatContextData;
